import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { CatalogSuggestionSource } from 'fym-shared/src/smartlinks/catalog';
import { musicServicesByMusicAPIID } from 'fym-shared/src/music-services/allMusicServices';
import { TypedMemo } from '../../common/types';
import { Cover } from '../../music24/components/primitives/Cover';
import { SMLButton } from '../../music24/components/primitives/SMLButton';
import { IntegrationTypeIcon } from '../../music24/components/IntegrationTypeIcon/IntegrationTypeIcon';
import { CatalogSuggestion } from '../../../redux/api/fymAPI.types';
import { SuggestionMatchStrip } from './claimWay/SuggestionMatchStrip';
import { claimWayMessages } from './claimWay/claimWayMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole screen; each part uses its own slice of it */
import styles from './Catalog.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface SuggestionRowProps {
  suggestion: CatalogSuggestion;
  onDismiss: (catalogTrackMaid: string) => void;
  onClaim: (suggestion: CatalogSuggestion) => void;
  /** "Merge" on a suggestion with a match (design catalog-merge c1). */
  onMerge: (suggestion: CatalogSuggestion) => void;
  /** "Not the same": the row stays as a plain new release. */
  onNotTheSame: (catalogTrackMaid: string) => void;
  /** A merge of this row runs: its buttons lock. */
  isBusy: boolean;
}

/**
 * One candidate release.
 *
 * A catalog row is a name match, so it carries the figure that says how sure we
 * are. A profile row was read off the artist's own page, so it carries the page
 * it came from instead — a percentage there would read as a guess about work we
 * already know is theirs.
 */
const SuggestionRowComponent: React.FC<SuggestionRowProps> = ({
  suggestion,
  onDismiss,
  onClaim,
  onMerge,
  onNotTheSame,
  isBusy,
}) => {
  const intl = useIntl();
  const { catalogTrackMaid, title, artistName, albumName, artworkUrl, confidence, source, integrationType } =
    suggestion;

  const dismiss = useCallback(() => onDismiss(catalogTrackMaid), [catalogTrackMaid, onDismiss]);
  const claim = useCallback(() => onClaim(suggestion), [onClaim, suggestion]);
  const merge = useCallback(() => onMerge(suggestion), [onMerge, suggestion]);
  const notTheSame = useCallback(() => onNotTheSame(catalogTrackMaid), [catalogTrackMaid, onNotTheSame]);
  const match = suggestion.matchesCatalogItem ?? null;

  const subtitle = albumName === null || albumName === title ? artistName : `${artistName} · ${albumName}`;
  const isFromProfile = source === CatalogSuggestionSource.profile && integrationType !== null;
  const serviceName =
    integrationType === null ? '' : (musicServicesByMusicAPIID[integrationType]?.name ?? integrationType);

  return (
    <div className={styles.suggestion}>
      <Cover src={artworkUrl} label={title} size={38} />
      <div className={styles.suggestionInfo}>
        <div className={styles.suggestionName}>{title}</div>
        <div className={styles.suggestionSub}>{subtitle}</div>
      </div>
      {isFromProfile ? (
        <span className={styles.suggestionBadge}>
          <IntegrationTypeIcon integrationType={integrationType} size={13} />
          {intl.formatMessage(
            { id: 'app.sml.catalog.suggestions.from_profile', defaultMessage: 'From your {service} profile' },
            { service: serviceName }
          )}
        </span>
      ) : (
        <span className={styles.suggestionConfidence}>
          {intl.formatMessage(
            { id: 'app.sml.catalog.suggestions.confidence', defaultMessage: '{percent}% match' },
            { percent: Math.round(confidence * 100) }
          )}
        </span>
      )}
      {match === null ? (
        <span className={styles.suggestionActions}>
          <SMLButton onClick={dismiss} className={styles.smallButton}>
            {intl.formatMessage({ id: 'app.sml.catalog.suggestions.dismiss', defaultMessage: 'Not mine' })}
          </SMLButton>
          <SMLButton variant="primary" onClick={claim} className={styles.smallButton}>
            {intl.formatMessage({ id: 'app.sml.catalog.suggestions.claim', defaultMessage: 'This is mine' })}
          </SMLButton>
        </span>
      ) : (
        <span className={styles.suggestionActions}>
          <SMLButton onClick={notTheSame} className={styles.smallButton} disabled={isBusy}>
            {intl.formatMessage(claimWayMessages.notTheSame)}
          </SMLButton>
          <SMLButton variant="primary" onClick={merge} className={styles.smallButton} disabled={isBusy}>
            {intl.formatMessage(claimWayMessages.merge)}
          </SMLButton>
        </span>
      )}
      {match !== null && <SuggestionMatchStrip match={match} serviceName={isFromProfile ? serviceName : undefined} />}
    </div>
  );
};

export const SuggestionRow = TypedMemo(SuggestionRowComponent);
